import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Link2, RefreshCw, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { api } from '@/lib/api';

interface UpworkStatus {
  connected: boolean;
  expiresAt?: string | null;
  isExpired?: boolean;
}

export function IntegrationsSettings() {
  const [upworkStatus, setUpworkStatus] = useState<UpworkStatus>({ connected: false });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    checkUpworkStatus();

    // Check for OAuth callback success/error
    const params = new URLSearchParams(window.location.search);
    const upworkParam = params.get('upwork');
    const messageParam = params.get('message');

    if (upworkParam === 'connected') {
      toast({
        title: 'Success',
        description: 'Upwork account connected successfully',
      });
      checkUpworkStatus();
      window.history.replaceState({}, '', window.location.pathname);
    } else if (upworkParam === 'error') {
      toast({
        title: 'Connection Failed',
        description: messageParam || 'Failed to connect Upwork account',
        variant: 'destructive',
      });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [toast]);

  async function checkUpworkStatus() {
    try {
      setLoading(true);
      const response = await api.get<UpworkStatus>('/upwork/status');
      setUpworkStatus(response);
    } catch (error) {
      console.error('Failed to check Upwork status:', error);
    } finally {
      setLoading(false);
    }
  }

  async function connectUpwork() {
    window.location.href = `${import.meta.env.VITE_API_URL}/upwork/auth`;
  }

  async function disconnectUpwork() {
    try {
      await api.delete('/upwork/disconnect');
      setUpworkStatus({ connected: false });
      toast({
        title: 'Disconnected',
        description: 'Upwork account disconnected successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to disconnect Upwork account',
        variant: 'destructive',
      });
    }
  }

  async function triggerSync() {
    try {
      setSyncing(true);
      await api.post('/upwork/webhooks/sync');
      toast({
        title: 'Sync Started',
        description: 'Upwork data synchronization has been triggered',
      });
    } catch (error) {
      toast({
        title: 'Sync Failed',
        description: 'Failed to trigger Upwork sync',
        variant: 'destructive',
      });
    } finally {
      setSyncing(false);
    }
  }

  function getStatusBadge() {
    if (!upworkStatus.connected) {
      return (
        <Badge variant="secondary" className="flex items-center gap-1">
          <XCircle className="w-3 h-3" />
          Not Connected
        </Badge>
      );
    }

    if (upworkStatus.isExpired) {
      return (
        <Badge variant="destructive" className="flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          Token Expired
        </Badge>
      );
    }

    return (
      <Badge variant="default" className="flex items-center gap-1 bg-green-500">
        <CheckCircle2 className="w-3 h-3" />
        Connected
      </Badge>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Integrations</h1>
        <p className="text-muted-foreground">Manage external platform integrations</p>
      </div>

      <div className="grid gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2">
                <Link2 className="w-5 h-5" />
                Upwork Integration
              </CardTitle>
              <CardDescription>
                Sync contracts, clients, time logs, and financial data from Upwork
              </CardDescription>
            </div>
            {loading ? (
              <Badge variant="outline">Loading...</Badge>
            ) : (
              getStatusBadge()
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {!loading && upworkStatus.connected && (
              <div className="space-y-2 text-sm text-muted-foreground">
                {upworkStatus.expiresAt && (
                  <p>
                    Token expires: {new Date(upworkStatus.expiresAt).toLocaleDateString()} at{' '}
                    {new Date(upworkStatus.expiresAt).toLocaleTimeString()}
                  </p>
                )}
                <p className="text-xs">
                  Automatic sync runs every 6 hours to keep your data up to date
                </p>
              </div>
            )}

            <div className="flex gap-2">
              {!upworkStatus.connected || upworkStatus.isExpired ? (
                <Button onClick={connectUpwork} disabled={loading}>
                  <Link2 className="w-4 h-4 mr-2" />
                  Connect Upwork Account
                </Button>
              ) : (
                <>
                  <Button onClick={triggerSync} disabled={syncing} variant="outline">
                    <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                    {syncing ? 'Syncing...' : 'Sync Now'}
                  </Button>
                  <Button onClick={disconnectUpwork} variant="destructive" disabled={loading}>
                    Disconnect
                  </Button>
                </>
              )}
            </div>

            {upworkStatus.connected && (
              <div className="bg-muted p-4 rounded-lg space-y-2">
                <h4 className="font-semibold text-sm">What gets synced:</h4>
                <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                  <li>Active contracts and their details</li>
                  <li>Client information and profiles</li>
                  <li>Team member (freelancer) data</li>
                  <li>Time logs for the last 30 days</li>
                  <li>Financial reports and earnings</li>
                </ul>
              </div>
            )}

            {!upworkStatus.connected && (
              <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 p-4 rounded-lg">
                <h4 className="font-semibold text-sm mb-2">Setup Instructions:</h4>
                <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                  <li>Click "Connect Upwork Account" above</li>
                  <li>Log in to your Upwork account</li>
                  <li>Authorize LWM CRM to access your data</li>
                  <li>You'll be redirected back here upon successful connection</li>
                </ol>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Placeholder for future integrations */}
        <Card className="opacity-60">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5" />
              Zoho CRM Integration
            </CardTitle>
            <CardDescription>
              Import existing data from Zoho CRM
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Badge variant="outline">Coming Soon</Badge>
          </CardContent>
        </Card>

        <Card className="opacity-60">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5" />
              Email Integration
            </CardTitle>
            <CardDescription>
              Connect Gmail or Outlook for email tracking
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Badge variant="outline">Coming Soon</Badge>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default IntegrationsSettings;
